import { ECrewAiAgentMemoryMode, IAiAgentTemplate, IAiTemplate } from "@kuika/kuika-cl-model";
import * as React from "react";
import ReactFlow, {
  Background,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
  useUpdateNodeInternals
} from "reactflow";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import {
  AI_AGENT_MANAGER_SOURCE_HANDLE_ID,
  generateAIAgentNodesAndEdgesWithProcessType
} from "../ai-agent/ai-agent-helper";
import SharedReactFlowControls from "../common/react-flow-controls/react-flow-controls";
import "./ai-action-template-detail-modal.scss";

export interface IAIActionTemplateDetailModalProps {
  visible: boolean;
  template: IAiTemplate | IAiAgentTemplate | null;
  modelName?: string;
  isSelected: boolean;
  isEditable?: boolean;
  onOk: () => void;
  onEdit?: () => void;
  onCancel: () => void;
}

export interface IAIActionTemplateDetailModalState {
  expandedAgentKeys: string[];
  activeAccordionKeys: string[];
  expandedReturnTypeKeys: string[];
  expandedCustomTypeKeys: string[];
  expandedNestedTypeKeys: Set<string>;
  isDescriptionExpanded: boolean;
  nodes: any[];
  edges: any[];
  isLoadingFlow: boolean;
}

// Read-only node component for ReactFlow
const ReadOnlyAgentNode = (props: any) => {
  const { id, data } = props;
  const metadata = data.metadata;
  const updateNodeInternals = useUpdateNodeInternals();

  React.useEffect(() => {
    // Force update internals to ensure handles are positioned correctly after render
    const timer = setTimeout(() => {
      updateNodeInternals(id);
    }, 100);
    return () => clearTimeout(timer);
  }, [id, updateNodeInternals]);

  return (
    <>
      <div className="handles targets">
        {data.targetHandles?.map((handle: any) => (
          <Handle key={handle.id} id={handle.id} type="target" position={Position.Left} />
        ))}
      </div>

      <div className="ai-template-detail__flow-node">
        <div className="ai-template-detail__flow-node-header">
          <div className="ai-template-detail__flow-node-title">{metadata.name}</div>
        </div>
        <div className="ai-template-detail__flow-node-body">
          <div className="ai-template-detail__flow-node-section">
            <div className="ai-template-detail__flow-node-label">Role</div>
            <div className="ai-template-detail__flow-node-text">{metadata.role}</div>
          </div>
          <div className="ai-template-detail__flow-node-section">
            <div className="ai-template-detail__flow-node-label">Goal</div>
            <div className="ai-template-detail__flow-node-text">{metadata.goal}</div>
          </div>
          {metadata.tools && metadata.tools.length > 0 && (
            <div className="ai-template-detail__flow-node-section">
              <div className="ai-template-detail__flow-node-label">Tools</div>
              <div className="ai-template-detail__flow-node-tools">
                {metadata.tools.map((tool: any, idx: number) => (
                  <span key={idx} className="ai-template-detail__flow-node-tool-badge">
                    {tool.displayName || tool.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="handles sources">
        {data.sourceHandles?.map((handle: any) => (
          <Handle key={handle.id} id={handle.id} type="source" position={Position.Right} />
        ))}
      </div>
    </>
  );
};

const ReadOnlyManagerNode = (props: any) => {
  const { data } = props;

  return (
    <>
      <div className="ai-template-detail__flow-manager-node">
        <div className="ai-template-detail__flow-manager-node-header">
          <KuikaSvg type="datasource-icons" name="brain" width={12} height={12} color="var(--orange-8)" />
          <span className="ai-template-detail__flow-manager-node-title">Manager Agent</span>
        </div>
        <div className="ai-template-detail__flow-manager-node-body">
          <div className="ai-template-detail__flow-manager-row">
            <span className="ai-template-detail__flow-manager-label">Model</span>
            <span className="ai-template-detail__flow-manager-value">
              {data.managerAgent?.model || "Not configured"}
            </span>
          </div>
          <div className="ai-template-detail__flow-manager-row">
            <span className="ai-template-detail__flow-manager-label">Memory</span>
            <span className="ai-template-detail__flow-manager-value">
              {data.managerAgent?.memoryMode === undefined
                ? "Not set"
                : data.managerAgent.memoryMode === ECrewAiAgentMemoryMode.Shared
                  ? "Shared"
                  : data.managerAgent.memoryMode === ECrewAiAgentMemoryMode.Isolated
                    ? "Isolated"
                    : "Disabled"}
            </span>
          </div>
        </div>
      </div>
      <Handle id={AI_AGENT_MANAGER_SOURCE_HANDLE_ID} type="source" position={Position.Right} />
    </>
  );
};

// Define nodeTypes outside the class component to prevent recreation
const NODE_TYPES = {
  elk: ReadOnlyAgentNode,
  manager: ReadOnlyManagerNode
};

// Wrapper component to use ReactFlow hooks
const AgentWorkflowDiagram = ({ nodes, edges, visible }: { nodes: any[]; edges: any[]; visible: boolean }) => {
  const reactFlowInstance = useReactFlow();

  React.useEffect(() => {
    if (!visible || nodes.length === 0) return;

    // Delay fitView to ensure container is fully rendered and visible
    const timer = setTimeout(() => {
      reactFlowInstance.fitView({
        padding: 0.5,
        includeHiddenNodes: false,
        duration: 200
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [nodes, edges, visible, reactFlowInstance]);

  return (
    <>
      <Background />
      <SharedReactFlowControls />
    </>
  );
};

// Custom Accordion Item Component
interface AccordionItemProps {
  title: string | React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  noBorder?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, isOpen, onToggle, children, noBorder }) => {
  const contentClassNames: string[] = ["ai-template-detail__accordion-content"];
  if (isOpen) {
    contentClassNames.push("ai-template-detail__accordion-content--open");
  }
  if (noBorder) {
    contentClassNames.push("ai-template-detail__accordion-content--no-border");
  }

  return (
    <div className={`ai-template-detail__accordion-item ${isOpen ? "ai-template-detail__accordion-item--active" : ""}`}>
      <div className="ai-template-detail__accordion-header" onClick={onToggle}>
        <KuikaSvg
          type="arrows"
          name="chevron-down"
          width={14}
          height={14}
          style={{
            transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease"
          }}
        />
        <div className="ai-template-detail__accordion-label">{title}</div>
      </div>
      <div className={contentClassNames.join(" ")}>
        <div className="ai-template-detail__accordion-content-inner">{children}</div>
      </div>
    </div>
  );
};

class AIActionTemplateDetailModal extends React.PureComponent<
  IAIActionTemplateDetailModalProps,
  IAIActionTemplateDetailModalState
> {
  constructor(props: IAIActionTemplateDetailModalProps) {
    super(props);
    this.state = {
      expandedAgentKeys: [],
      activeAccordionKeys: ["parameters", "returnType", "inputs", "workflow", "customTypes"],
      expandedReturnTypeKeys: [],
      expandedCustomTypeKeys: [],
      expandedNestedTypeKeys: new Set<string>(),
      isDescriptionExpanded: false,
      nodes: [],
      edges: [],
      isLoadingFlow: false
    };
  }

  async componentDidMount() {
    await this.loadAgentFlow();
  }

  async componentDidUpdate(prevProps: IAIActionTemplateDetailModalProps) {
    if (prevProps.template !== this.props.template || prevProps.visible !== this.props.visible) {
      await this.loadAgentFlow();
    }
  }

  loadAgentFlow = async () => {
    const { template, visible } = this.props;
    if (!visible || !this.isAgentTemplate(template)) {
      this.setState({ nodes: [], edges: [], isLoadingFlow: false });
      return;
    }

    const agentModel = this.parseJsonString(template.agentModel);
    if (!agentModel || !agentModel.crewAgents || agentModel.crewAgents.length === 0) {
      this.setState({ nodes: [], edges: [], isLoadingFlow: false });
      return;
    }

    try {
      this.setState({ isLoadingFlow: true });
      const processType = agentModel.processType === "hierarchical" ? "hierarchical" : "sequential";
      const managerAgent = agentModel.managerAgent ?? agentModel.manager_agent;
      const { nodes, edges } = await generateAIAgentNodesAndEdgesWithProcessType(
        agentModel.crewAgents,
        processType,
        managerAgent
      );
      this.setState({ nodes, edges, isLoadingFlow: false });
    } catch (error) {
      console.error("Error generating agent flow:", error);
      this.setState({ nodes: [], edges: [], isLoadingFlow: false });
    }
  };

  private isAgentTemplate = (template: any): template is IAiAgentTemplate => {
    return template && "agentModel" in template && template.agentModel !== null && template.agentModel !== undefined;
  };

  private isActionTemplate = (template: any): template is IAiTemplate => {
    return template && !this.isAgentTemplate(template);
  };

  private getTypeBadge = (typeName?: string | null): { label: string; className: string } => {
    const defaultBadge = {
      label: typeName || "Unknown",
      className: "ai-template-detail__type-badge--default"
    };

    if (!typeName) {
      return defaultBadge;
    }

    const normalized = typeName.toLowerCase();

    if (normalized.includes("string") || normalized.includes("text")) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--text"
      };
    }

    if (/(int|long|double|float|decimal|number)/.test(normalized)) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--numeric"
      };
    }

    if (normalized.includes("bool")) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--boolean"
      };
    }

    if (normalized.includes("date") || normalized.includes("time")) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--datetime"
      };
    }

    if (normalized.includes("list") || normalized.includes("array") || normalized.includes("collection")) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--collection"
      };
    }

    if (normalized.includes("json") || normalized.includes("object") || normalized.includes("model")) {
      return {
        label: typeName,
        className: "ai-template-detail__type-badge--object"
      };
    }

    return defaultBadge;
  };

  private getBadgeColor = (className: string): "blue" | "yellow" | "green" | "purple" | "orange" | "pink" => {
    if (className.includes("text")) return "purple";
    if (className.includes("numeric")) return "orange";
    if (className.includes("boolean")) return "green";
    if (className.includes("datetime")) return "blue";
    if (className.includes("collection")) return "pink";
    if (className.includes("object")) return "yellow";
    return "blue";
  };

  private cleanTypeName = (typeName: string): string => {
    // Remove prefixes like "AI_CvResult_" or similar patterns
    // Match pattern: word_word_actualName -> actualName
    const parts = typeName.split("_");
    if (parts.length > 2) {
      // If we have more than 2 parts, take the last part as the actual type name
      return parts[parts.length - 1];
    }
    return typeName;
  };

  private parsePropertyValue = (value: string): { datatype: string; description: string | null } => {
    // Parse format: "datatype (description)"
    const match = value.match(/^([^(]+)\s*\(([^)]+)\)$/);
    if (match) {
      return {
        datatype: match[1].trim(),
        description: match[2].trim()
      };
    }
    return {
      datatype: value,
      description: null
    };
  };

  parseJsonString = (jsonString: string | null): any => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  renderParametersContent = () => {
    const { template } = this.props;
    if (!this.isActionTemplate(template)) return null;

    const parameters = this.parseJsonString(template?.parameters || null);
    if (!parameters || !Array.isArray(parameters) || parameters.length === 0) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No parameters defined</span>
        </div>
      );
    }

    return (
      <div className="ai-template-detail__params-list">
        {parameters.map((param: any, index: number) => {
          const typeBadge = this.getTypeBadge(param.Typename || param.TypeName);
          return (
            <div key={index} className="ai-template-detail__param-item">
              <span className="ai-template-detail__param-name">{param.Name}</span>
              <KuikaBadge content={typeBadge.label} color={this.getBadgeColor(typeBadge.className)} />
            </div>
          );
        })}
      </div>
    );
  };

  toggleNestedType = (path: string) => {
    this.setState((prevState) => {
      const newExpandedKeys = new Set(prevState.expandedNestedTypeKeys);
      if (newExpandedKeys.has(path)) {
        newExpandedKeys.delete(path);
      } else {
        newExpandedKeys.add(path);
      }
      return { expandedNestedTypeKeys: newExpandedKeys };
    });
  };

  renderTypeElement = (element: any, depth: number = 0, parentPath: string = ""): JSX.Element => {
    const indent = depth * 4;
    const hasNestedElements = element.ElementTypeModel?.Elements;
    const isList = element.IsList;
    const typeBadge = this.getTypeBadge(element.TypeName);
    const cleanedTypeName = this.cleanTypeName(element.TypeName);
    const elementPath = parentPath ? `${parentPath}.${element.Name}` : element.Name;
    const isExpanded = this.state.expandedNestedTypeKeys.has(elementPath);
    const depthClass = `ai-template-detail__thread--depth-${Math.min(depth, 3)}`;

    // For list objects with nested elements, show "List: Name" format
    if (hasNestedElements) {
      return (
        <div key={elementPath} className={`ai-template-detail__thread-item ${depthClass}`}>
          <div
            className="ai-template-detail__thread-row ai-template-detail__thread-row--clickable"
            onClick={() => this.toggleNestedType(elementPath)}
          >
            <span className="ai-template-detail__thread-name">
              <KuikaSvg
                type="arrows"
                name="chevron-down"
                width={14}
                height={14}
                style={{
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s ease"
                }}
              />

              {element.Name}
            </span>
            <div className="ai-template-detail__thread-badges">
              <KuikaBadge content="Object" color="grey" />
              {isList && <KuikaBadge content="List" color="grey" />}
            </div>
          </div>
          {isExpanded && (
            <div className="ai-template-detail__thread-children">
              {element.ElementTypeModel.Elements.map((nested: any) =>
                this.renderTypeElement(nested, depth + 1, elementPath)
              )}
            </div>
          )}
        </div>
      );
    }

    // For simple elements or list of simple types
    const badgeLabel = isList ? `${cleanedTypeName}[]` : cleanedTypeName;
    const badgeColor = isList ? "pink" : this.getBadgeColor(typeBadge.className);
    const isRoot = depth === 0;

    return (
      <div key={element.Name} className="ai-template-detail__type-element">
        <div className="ai-template-detail__type-element-row">
          {isRoot ? (
            <>
              <span className="ai-template-detail__type-element-name" style={{ paddingLeft: `${indent}px` }}>
                {element.Name}
              </span>
              <KuikaBadge content={badgeLabel} color="grey" />
            </>
          ) : (
            <KuikaBadge content={`${element.Name} : ${badgeLabel}`} color="grey" />
          )}
        </div>
      </div>
    );
  };

  renderReturnTypeContent = () => {
    const { template } = this.props;
    if (!this.isActionTemplate(template) || !template?.returnTypeName) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No return type defined</span>
        </div>
      );
    }

    const returnTypeObj = this.parseJsonString(template.returnType || null);
    const isStringReturn = template.returnTypeName?.toLowerCase().includes("string");
    const hasNoStructure = !returnTypeObj || !returnTypeObj.Elements;
    const isExpanded = this.state.expandedReturnTypeKeys.includes("structure");

    return (
      <div className="ai-template-detail__return-type">
        {/* <div className="ai-template-detail__return-type-header">
          <KuikaBadge content={returnTypeBadge.label} color={this.getBadgeColor(returnTypeBadge.className)} />
        </div> */}
        {returnTypeObj && returnTypeObj.Elements && (
          <div className="ai-template-detail__return-elements">
            {/* <AccordionItem
              title="Type Structure"
              isOpen={isExpanded}
              onToggle={() => {
                this.setState({
                  expandedReturnTypeKeys: isExpanded ? [] : ["structure"]
                });
              }}
            > */}
            <div className="ai-template-detail__type-structure">
              {returnTypeObj.Elements.map((element: any) => this.renderTypeElement(element, 0, "returnType"))}
            </div>
            {/* </AccordionItem> */}
          </div>
        )}
        {isStringReturn && hasNoStructure && (
          <div className="ai-template-detail__return-type-warning">
            <p className="ai-template-detail__return-type-warning-text">
              This action returns a plain text response. The output will be a string containing the response generated
              by this AI action.
            </p>
          </div>
        )}
      </div>
    );
  };

  renderAgentInputsContent = () => {
    const { template } = this.props;
    if (!this.isAgentTemplate(template)) return null;

    const agentModel = this.parseJsonString(template.agentModel);
    if (!agentModel || !agentModel.crew_inputs || agentModel.crew_inputs.length === 0) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No inputs defined</span>
        </div>
      );
    }

    return (
      <div className="ai-template-detail__params-list">
        {agentModel.crew_inputs.map((input: any, index: number) => {
          const typeBadge = this.getTypeBadge(input.parameter_format);
          return (
            <div key={index} className="ai-template-detail__param-item">
              <span className="ai-template-detail__param-name">{input.parameter_name}</span>
              <KuikaBadge content={typeBadge.label} color={this.getBadgeColor(typeBadge.className)} />
            </div>
          );
        })}
      </div>
    );
  };

  renderAgentWorkflow = () => {
    const { template, visible } = this.props;
    const { nodes, edges, isLoadingFlow } = this.state;

    if (!this.isAgentTemplate(template)) return null;

    const agentModel = this.parseJsonString(template.agentModel);
    if (!agentModel || !agentModel.crewAgents || agentModel.crewAgents.length === 0) {
      return <span className="ai-template-detail__empty">No agents defined</span>;
    }

    if (isLoadingFlow) {
      return <div className="ai-template-detail__flow-loading">Loading workflow diagram...</div>;
    }

    return (
      <div className="ai-template-detail__agent-workflow" key={`flow-${visible ? "visible" : "hidden"}`}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            minZoom={0.1}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
            nodesFocusable={true}
            edgesFocusable={false}
            edgesUpdatable={false}
            elementsSelectable={true}
            panOnScroll={true}
            zoomOnScroll={true}
            panOnDrag={true}
            preventScrolling={false}
            fitView
            fitViewOptions={{ padding: 0.5, duration: 0 }}
          >
            <AgentWorkflowDiagram nodes={nodes} edges={edges} visible={visible} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    );
  };

  renderCustomDataTypes = () => {
    const { template } = this.props;
    if (!this.isAgentTemplate(template)) return null;

    const agentModel = this.parseJsonString(template.agentModel);
    if (!agentModel || !agentModel.crew_custom_data_types || agentModel.crew_custom_data_types.length === 0) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No custom data types defined</span>
        </div>
      );
    }

    return (
      <div className="ai-template-detail__thread-root">
        {agentModel.crew_custom_data_types.map((dataType: any, index: number) => {
          const hasProperties = dataType.properties && Object.keys(dataType.properties).length > 0;
          const dataTypePath = `customType-${index}`;
          const isExpanded = this.state.expandedNestedTypeKeys.has(dataTypePath);

          return (
            <div key={index} className="ai-template-detail__thread-item ai-template-detail__thread--depth-0">
              <div
                className="ai-template-detail__thread-row"
                onClick={() => hasProperties && this.toggleNestedType(dataTypePath)}
                style={{ cursor: hasProperties ? "pointer" : "default" }}
              >
                <span className="ai-template-detail__thread-name">
                  {hasProperties && (
                    <KuikaSvg
                      type="arrows"
                      name="chevron-down"
                      width={14}
                      height={14}
                      style={{
                        transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s ease"
                      }}
                    />
                  )}
                  {dataType.type_name}
                </span>
                <div className="ai-template-detail__thread-badges">
                  <KuikaBadge content="Object" color="yellow" />
                </div>
              </div>
              {hasProperties && isExpanded && (
                <div className="ai-template-detail__thread-children">
                  {Object.entries(dataType.properties).map(([key, value]: [string, any], propIndex: number) => {
                    const parsed = this.parsePropertyValue(String(value));
                    const typeBadge = this.getTypeBadge(parsed.datatype);

                    return (
                      <div
                        key={propIndex}
                        className="ai-template-detail__thread-item ai-template-detail__thread--depth-1"
                      >
                        <div className="ai-template-detail__thread-row">
                          <span className="ai-template-detail__thread-name">{key}</span>
                          <KuikaBadge content={parsed.datatype} color={this.getBadgeColor(typeBadge.className)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const { visible, template, modelName, isEditable, onOk, onEdit, onCancel } = this.props;

    if (!visible || !template) return null;

    return (
      <div className="ai-template-detail-overlay" onClick={onCancel}>
        <div className="ai-template-detail-panel" onClick={(e) => e.stopPropagation()}>
          <div className="ai-template-detail-panel__header">
            <div className="ai-template-detail-panel__header-title">Continue with this template</div>
            <div className="ai-template-detail-panel__header-left">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                aria-label="Close template details"
                onClick={onCancel}
              >
                <KuikaSvg type="common" name="x" width={14} height={14} color="var(--dark-7)" />
              </KuikaButton>
            </div>
          </div>
          <div className="ai-template-detail-panel__body">
            <div className="ai-template-detail__header">
              <img
                src={template.imageUrl || "/images/ai-template-thumb.png"}
                alt={template.name}
                className="ai-template-detail__image"
              />
              <div className="ai-template-detail__header-info">
                <div className="ai-template-detail-panel__header-title">
                  <div className="ai-template-detail-panel__title">{KMainFunctions.formatCamelCase(template.name)}</div>
                  {modelName && this.isActionTemplate(template) && (
                    <div className="ai-template-detail__model-badge">
                      <KuikaBadge content={modelName} color="purple" />
                    </div>
                  )}
                </div>

                <div className="ai-template-detail__description-wrapper">
                  <p
                    className={`ai-template-detail__description ${
                      !this.state.isDescriptionExpanded ? "ai-template-detail__description--truncated" : ""
                    }`}
                  >
                    {template.description || "No description provided for this template."}
                  </p>
                  {template.description && template.description.length > 150 && (
                    <span
                      className="ai-template-detail__description-toggle"
                      onClick={() =>
                        this.setState({
                          isDescriptionExpanded: !this.state.isDescriptionExpanded
                        })
                      }
                    >
                      {this.state.isDescriptionExpanded ? "Less" : "More..."}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="ai-template-detail__content">
              {this.isActionTemplate(template) ? (
                <div className="ai-template-detail__accordion">
                  <AccordionItem
                    title="Parameters"
                    isOpen={this.state.activeAccordionKeys.includes("parameters")}
                    onToggle={() => {
                      const isOpen = this.state.activeAccordionKeys.includes("parameters");
                      this.setState({
                        activeAccordionKeys: isOpen
                          ? this.state.activeAccordionKeys.filter((k) => k !== "parameters")
                          : [...this.state.activeAccordionKeys, "parameters"]
                      });
                    }}
                  >
                    {this.renderParametersContent()}
                  </AccordionItem>
                  <AccordionItem
                    title={
                      <div className="ai-template-detail__accordion-title">
                        <span>Return Type</span>
                        {this.isActionTemplate(template) && template.returnTypeName && (
                          <KuikaBadge
                            content={this.getTypeBadge(template.returnTypeName).label}
                            color={this.getBadgeColor(this.getTypeBadge(template.returnTypeName).className)}
                          />
                        )}
                      </div>
                    }
                    isOpen={this.state.activeAccordionKeys.includes("returnType")}
                    onToggle={() => {
                      const isOpen = this.state.activeAccordionKeys.includes("returnType");
                      this.setState({
                        activeAccordionKeys: isOpen
                          ? this.state.activeAccordionKeys.filter((k) => k !== "returnType")
                          : [...this.state.activeAccordionKeys, "returnType"]
                      });
                    }}
                  >
                    {this.renderReturnTypeContent()}
                  </AccordionItem>
                </div>
              ) : (
                <div className="ai-template-detail__accordion">
                  <AccordionItem
                    title="Agent Workflow"
                    noBorder
                    isOpen={this.state.activeAccordionKeys.includes("workflow")}
                    onToggle={() => {
                      const isOpen = this.state.activeAccordionKeys.includes("workflow");
                      this.setState({
                        activeAccordionKeys: isOpen
                          ? this.state.activeAccordionKeys.filter((k) => k !== "workflow")
                          : [...this.state.activeAccordionKeys, "workflow"]
                      });
                    }}
                  >
                    {this.renderAgentWorkflow()}
                  </AccordionItem>
                  <AccordionItem
                    title="Inputs"
                    isOpen={this.state.activeAccordionKeys.includes("inputs")}
                    onToggle={() => {
                      const isOpen = this.state.activeAccordionKeys.includes("inputs");
                      this.setState({
                        activeAccordionKeys: isOpen
                          ? this.state.activeAccordionKeys.filter((k) => k !== "inputs")
                          : [...this.state.activeAccordionKeys, "inputs"]
                      });
                    }}
                  >
                    {this.renderAgentInputsContent()}
                  </AccordionItem>

                  <AccordionItem
                    title="Custom Data Types"
                    isOpen={this.state.activeAccordionKeys.includes("customTypes")}
                    onToggle={() => {
                      const isOpen = this.state.activeAccordionKeys.includes("customTypes");
                      this.setState({
                        activeAccordionKeys: isOpen
                          ? this.state.activeAccordionKeys.filter((k) => k !== "customTypes")
                          : [...this.state.activeAccordionKeys, "customTypes"]
                      });
                    }}
                  >
                    {this.renderCustomDataTypes()}
                  </AccordionItem>
                </div>
              )}
            </div>
          </div>
          <div className="ai-template-detail-panel__footer">
            {isEditable && onEdit && (
              <KuikaButton type="secondary" size="md" uppercase onClick={onEdit}>
                Edit
              </KuikaButton>
            )}
            <KuikaButton type="primary" size="md" uppercase onClick={onOk}>
              Start with this template
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  }
}

export default AIActionTemplateDetailModal;
